import React, { useEffect, useState } from "react";
import moment from "moment";
import { Card, CardContent, CardHeader } from "../../../components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from "../../../components/ui/table";

import pensionLookupTable from "../../../utils/pensionLookupTable";
import {
  fersNoPenaltyRules,
  fersEarliestRules,
} from "../../../utils/workbookRules";

// ============= DEBUG UTILITY =============
function debugClientData(client: any) {
  console.log("=== CLIENT DATA DEBUG ===");
  console.log("Client ID:", client.id);
  console.log("Client Type:", client.type);
  console.log("Client Retirement Plan:", client.retirementPlan);
  console.log("Date of Birth:", JSON.stringify(client.dateOfBirth));
  console.log("Date of Birth Type:", typeof client.dateOfBirth);
  console.log(
    "Service Computation Date:",
    JSON.stringify(client.serviceComputationDate)
  );
  console.log(
    "Service Computation Date Type:",
    typeof client.serviceComputationDate
  );

  // Add helpful guidance
  if (!client.dateOfBirth) {
    console.warn(
      "⚠️ MISSING DATA: Date of Birth is required for federal retirement calculations"
    );
    console.warn(
      "📝 SOLUTION: Go to Personal Information tab and fill in the Date of Birth field"
    );
  }

  if (!client.serviceComputationDate) {
    console.warn(
      "⚠️ MISSING DATA: Service Computation Date is required for federal retirement calculations"
    );
    console.warn(
      "📝 SOLUTION: Go to Financial Information tab and fill in the Service Computation Date field"
    );
  }

  console.log("=== END CLIENT DATA DEBUG ===");
}

// ============= DATE PARSING UTILITY =============
function parseDateWithMultipleFormats(
  dateString: string | null | undefined
): moment.Moment | null {
  if (dateString === null || dateString === undefined) {
    console.log(
      "DOB DEBUG: parseDateWithMultipleFormats - dateString is null/undefined"
    );
    return null;
  }

  if (typeof dateString !== "string") {
    console.log(
      "DOB DEBUG: parseDateWithMultipleFormats - dateString is not a string, type:",
      typeof dateString
    );
    return null;
  }

  // Trim whitespace and check for empty string
  const trimmedDate = dateString.trim();
  if (!trimmedDate) {
    console.log(
      "DOB DEBUG: parseDateWithMultipleFormats - dateString is empty after trim"
    );
    return null;
  }

  console.log(
    "DOB DEBUG: parseDateWithMultipleFormats - input:",
    JSON.stringify(trimmedDate)
  );

  const formats = [
    "YYYY-MM-DD",
    "MM/DD/YYYY",
    "DD/MM/YYYY",
    "YYYY/MM/DD",
    "MM-DD-YYYY",
    "DD-MM-YYYY",
    "YYYY-MM-DDTHH:mm:ss.SSSZ", // ISO format with timezone
    "YYYY-MM-DDTHH:mm:ssZ", // ISO format without milliseconds
    "YYYY-MM-DDTHH:mm:ss", // ISO format without timezone
  ];

  // Try default parsing first
  let parsedMoment = moment(trimmedDate);
  if (parsedMoment.isValid()) {
    console.log(
      "DOB DEBUG: parseDateWithMultipleFormats - default parsing successful:",
      parsedMoment.format()
    );
    return parsedMoment;
  }

  // Try alternative formats
  for (const format of formats) {
    parsedMoment = moment(trimmedDate, format, true);
    if (parsedMoment.isValid()) {
      console.log(
        "DOB DEBUG: parseDateWithMultipleFormats - format parsing successful:",
        format,
        parsedMoment.format()
      );
      return parsedMoment;
    }
  }

  console.log(
    "DOB DEBUG: parseDateWithMultipleFormats - no valid format found"
  );
  return null;
}

// ============= EXCEPTION HELPER =============
function isDOBInvalid(dob: string | null | undefined): boolean {
  // Handle null, undefined, or empty values
  if (dob === null || dob === undefined) {
    console.log("DOB DEBUG: dateOfBirth is null/undefined");
    return true;
  }

  // Handle empty string or whitespace-only string
  if (typeof dob === "string" && dob.trim() === "") {
    console.log("DOB DEBUG: dateOfBirth is empty string or whitespace");
    return true;
  }

  // Handle non-string types
  if (typeof dob !== "string") {
    console.log("DOB DEBUG: dateOfBirth is not a string, type:", typeof dob);
    return true;
  }

  // Debug logging to understand the date format
  console.log("DOB DEBUG: Raw dateOfBirth value:", JSON.stringify(dob));
  console.log("DOB DEBUG: Type of dateOfBirth:", typeof dob);
  console.log("DOB DEBUG: Length of dateOfBirth:", dob.length);

  const parsedDate = parseDateWithMultipleFormats(dob);
  const isValid = parsedDate !== null;

  if (parsedDate) {
    console.log("DOB DEBUG: Parsed moment object:", parsedDate.format());
    console.log("DOB DEBUG: Parsed moment isValid:", parsedDate.isValid());
  }
  console.log("DOB DEBUG: Is valid:", isValid);

  if (!isValid) {
    console.log("DOB DEBUG: No valid format found for date:", dob);
    console.log("DOB DEBUG: Attempting to parse with moment directly...");

    // Try direct moment parsing as fallback
    const directMoment = moment(dob);
    console.log(
      "DOB DEBUG: Direct moment parsing result:",
      directMoment.format()
    );
    console.log("DOB DEBUG: Direct moment isValid:", directMoment.isValid());
  }

  return !isValid;
}

// ============= FERS HELPERS =============
function getMRA(dateOfBirth: string | null | undefined): number {
  if (!dateOfBirth) return 57;

  const birthMoment = parseDateWithMultipleFormats(dateOfBirth);

  // If still invalid, return default
  if (!birthMoment) {
    console.warn(
      "DOB WARNING: Could not parse dateOfBirth for MRA calculation:",
      dateOfBirth
    );
    return 57;
  }

  const year = birthMoment.year();
  // Example approach - you can refine if needed
  if (year < 1953) return 55;
  if (year < 1970) return 56;
  return 57;
}

function getYearsOfService(scd: string | null | undefined): number {
  if (!scd) return 0;

  const startMoment = parseDateWithMultipleFormats(scd);

  if (!startMoment) {
    console.warn("SCD WARNING: Could not parse service computation date:", scd);
    return 0;
  }

  return moment().diff(startMoment, "years");
}

/**
 * For the FERS “no penalty” column,
 * we iterate over fersNoPenaltyRules and pick the first matching threshold.
 */
function computeEarliestNoPenaltyAge(
  totalServiceNow: number,
  dob: string | null | undefined
): string {
  for (const rule of fersNoPenaltyRules) {
    if (totalServiceNow >= rule.minService) {
      if (rule.retireAge === "MRA") {
        return getMRA(dob).toString();
      } else {
        return rule.retireAge.toString();
      }
    }
  }
  return "N/A";
}

/**
 * For the FERS “earliest possible” (with penalty),
 * we iterate over fersEarliestRules similarly.
 */
function computeEarliestPossibleAge(
  totalServiceNow: number,
  dob: string | null | undefined
): string {
  for (const rule of fersEarliestRules) {
    if (totalServiceNow >= rule.minService) {
      if (rule.retireAge === "MRA") {
        return getMRA(dob).toString();
      } else {
        return rule.retireAge.toString();
      }
    }
  }
  return "N/A";
}

// ============= PENSION + SERVICE CALC =============
/**
 * Look up a pension from the table, given “income” and years-of-service.
 */
function getPensionValue(income: number, yearsOfService: number): number {
  const bracketKeys = Object.keys(pensionLookupTable).map(Number);
  const closestIncome = bracketKeys.reduce((prev, curr) =>
    Math.abs(curr - income) < Math.abs(prev - income) ? curr : prev
  );
  const row = pensionLookupTable[closestIncome];
  if (!row) return 0;
  return typeof row[yearsOfService] === "number" ? row[yearsOfService] : 0;
}

/**
 * Return a string like "$1,234.56".
 */
function formatCurrency(value: number): string {
  if (isNaN(value)) return "N/A";
  return `$${value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
}

/**
 * Given a final numeric monthlyIncomeAt62 and a string like "XXy YYm ZZd",
 * parse out the integer years and use getPensionValue(...) to find a table match.
 */
function lookupMonthlyIncomeFromPension(
  monthlyIncomeAt62: number | undefined,
  serviceString: string
): string {
  if (!monthlyIncomeAt62) return "N/A";
  const years = parseInt(serviceString.split("y")[0] || "0", 10);
  const val = getPensionValue(monthlyIncomeAt62, years);
  return formatCurrency(val);
}

/**
 * Calculate the retirement date from "DOB + scenarioAge".
 */
function calculateRetirementDate(
  scenarioAge: number,
  dob: string | null | undefined
): string {
  if (!dob) return "N/A";
  const m = parseDateWithMultipleFormats(dob);
  if (!m) return "N/A";
  return m.clone().add(scenarioAge, "years").format("MM/DD/YYYY");
}

/**
 * Calculate how many years, months, days from the SCD to the "scenarioRetDate".
 * This ensures each column uses a distinct retirement date for years-of-service.
 */
function calculateServiceAtRetirementDate(
  scd: string | null | undefined,
  retDateString: string
): string {
  if (!scd) return "N/A";
  const scdMoment = parseDateWithMultipleFormats(scd);
  if (!scdMoment) return "N/A";

  const retMoment = parseDateWithMultipleFormats(retDateString);
  if (!retMoment) return "N/A";

  // measure scd -> ret
  const years = retMoment.diff(scdMoment, "years");
  scdMoment.add(years, "years");
  const months = retMoment.diff(scdMoment, "months");
  scdMoment.add(months, "months");
  const days = retMoment.diff(scdMoment, "days");
  return `${years}y ${months}m ${days}d`;
}

// ============= REACT COMPONENT =============
interface FederalInformationTableProps {
  client: ClientType; // or your typed version with FERS fields
}

const FederalInformationTable: React.FC<FederalInformationTableProps> = ({
  client,
}) => {
  const keys = [
    "retirementAge",
    "retirementDate",
    "qualifyingYearsOfService",
    "creditingYearsOfService",
    "distributionStartAge",
    "distributionStartDate",
    "monthlyIncomeFromPension",
  ] as const;

  const [data, setData] = useState<{
    currentSelection: Record<(typeof keys)[number], string>;
    earliestWithIncomeAndNoPenalty: Record<(typeof keys)[number], string>;
    earliestPossible: Record<(typeof keys)[number], string>;
  }>({
    currentSelection: makeEmptyRow(),
    earliestWithIncomeAndNoPenalty: makeEmptyRow(),
    earliestPossible: makeEmptyRow(),
  });

  useEffect(() => {
    // Debug client data
    debugClientData(client);

    // If not a "federal/fers" plan, skip
    if (client.type !== "federal" || client.retirementPlan !== "fers") {
      setData({
        currentSelection: makeEmptyRow(),
        earliestWithIncomeAndNoPenalty: makeEmptyRow(),
        earliestPossible: makeEmptyRow(),
      });
      return;
    }

    // If DOB is missing/invalid => exception
    if (isDOBInvalid(client.dateOfBirth)) {
      console.warn(
        "⚠️ FEDERAL RETIREMENT CALCULATION: Date of Birth is required"
      );
      console.warn(
        "📝 ACTION NEEDED: Please fill in the client's Date of Birth in Personal Information"
      );
      console.warn(
        "🔍 DEBUG INFO - client.dateOfBirth value:",
        JSON.stringify(client.dateOfBirth)
      );
      console.warn(
        "🔍 DEBUG INFO - client.dateOfBirth type:",
        typeof client.dateOfBirth
      );

      const row = makeEmptyRow();

      // Provide more specific error messages based on the issue
      if (client.dateOfBirth === null || client.dateOfBirth === undefined) {
        row.retirementAge = "⚠️ DOB REQUIRED";
        row.retirementDate = "⚠️ DOB REQUIRED";
      } else if (typeof client.dateOfBirth !== "string") {
        row.retirementAge = "⚠️ DOB INVALID TYPE";
        row.retirementDate = "⚠️ DOB INVALID TYPE";
      } else {
        row.retirementAge = "⚠️ DOB INVALID FORMAT";
        row.retirementDate = "⚠️ DOB INVALID FORMAT";
      }
      setData({
        currentSelection: { ...row },
        earliestWithIncomeAndNoPenalty: { ...row },
        earliestPossible: { ...row },
      });
      return;
    }

    /** 1) Current Selection Column **/
    // e.g. user’s own “targetRetirementAge” if present, else fallback to 62
    const userSelectedAge =
      client.targetRetirementAge && client.targetRetirementAge > 0
        ? client.targetRetirementAge
        : 62;
    const userSelectedRetDate = calculateRetirementDate(
      userSelectedAge,
      client.dateOfBirth
    );
    // compute service from SCD => that date
    const userSelectedServiceStr = calculateServiceAtRetirementDate(
      client.serviceComputationDate,
      userSelectedRetDate
    );
    const userDistAgeStr =
      client.ageToStartDistributionsAt && client.ageToStartDistributionsAt > 0
        ? client.ageToStartDistributionsAt.toString()
        : "N/A";
    const userDistDateStr =
      userDistAgeStr !== "N/A"
        ? calculateRetirementDate(
            parseInt(userDistAgeStr, 10),
            client.dateOfBirth
          )
        : "N/A";

    const currentSelection = {
      retirementAge: userSelectedAge.toString(),
      retirementDate: userSelectedRetDate,
      qualifyingYearsOfService: userSelectedServiceStr,
      creditingYearsOfService: userSelectedServiceStr,
      distributionStartAge: userDistAgeStr,
      distributionStartDate: userDistDateStr,
      monthlyIncomeFromPension: lookupMonthlyIncomeFromPension(
        client.monthlyIncomeAt62,
        userSelectedServiceStr
      ),
    };

    /** 2) Earliest with No Penalty **/
    // We first see how many years-of-service they have *as of "today"*
    // just to see if they meet the 5/20/30 threshold (or 30=>MRA).
    const serviceNow = getYearsOfService(client.serviceComputationDate);
    const noPenaltyStr = computeEarliestNoPenaltyAge(
      serviceNow,
      client.dateOfBirth
    );
    const noPenaltyAge =
      noPenaltyStr === "N/A" ? 0 : parseInt(noPenaltyStr, 10);
    const noPenaltyRetDate = noPenaltyAge
      ? calculateRetirementDate(noPenaltyAge, client.dateOfBirth)
      : "N/A";
    const noPenaltyServiceStr = noPenaltyAge
      ? calculateServiceAtRetirementDate(
          client.serviceComputationDate,
          noPenaltyRetDate
        )
      : "N/A";

    const earliestWithIncomeAndNoPenalty = {
      retirementAge: noPenaltyStr,
      retirementDate: noPenaltyRetDate,
      qualifyingYearsOfService: noPenaltyServiceStr,
      creditingYearsOfService: noPenaltyServiceStr,
      distributionStartAge: noPenaltyStr,
      distributionStartDate: noPenaltyRetDate,
      monthlyIncomeFromPension:
        noPenaltyServiceStr !== "N/A"
          ? lookupMonthlyIncomeFromPension(
              client.monthlyIncomeAt62,
              noPenaltyServiceStr
            )
          : "N/A",
    };

    /** 3) Earliest Possible (with penalty) **/
    const earliestStr = computeEarliestPossibleAge(
      serviceNow,
      client.dateOfBirth
    );
    const earliestAge = earliestStr === "N/A" ? 0 : parseInt(earliestStr, 10);
    const earliestRetDate = earliestAge
      ? calculateRetirementDate(earliestAge, client.dateOfBirth)
      : "N/A";
    const earliestServiceStr =
      earliestRetDate !== "N/A"
        ? calculateServiceAtRetirementDate(
            client.serviceComputationDate,
            earliestRetDate
          )
        : "N/A";

    const earliestPossible = {
      retirementAge: earliestStr,
      retirementDate: earliestRetDate,
      qualifyingYearsOfService: earliestServiceStr,
      creditingYearsOfService: earliestServiceStr,
      distributionStartAge: earliestStr,
      distributionStartDate: earliestRetDate,
      monthlyIncomeFromPension:
        earliestServiceStr !== "N/A"
          ? lookupMonthlyIncomeFromPension(
              client.monthlyIncomeAt62,
              earliestServiceStr
            )
          : "N/A",
    };

    setData({
      currentSelection,
      earliestWithIncomeAndNoPenalty,
      earliestPossible,
    });
  }, [client]);

  // Render the table
  return (
    <Card className="mb-8" id="federal-information">
      <CardHeader>
        <h4 className="text-lg font-semibold text-gray-700">
          Federal Information
        </h4>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-100">
                <TableHead />
                <TableHead>Current Selection</TableHead>
                <TableHead>Earliest with Income &amp; No Penalty</TableHead>
                <TableHead>Earliest Possible</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key}>
                  <TableCell className="capitalize">
                    {key.replace(/([A-Z])/g, " $1")}
                  </TableCell>
                  <TableCell>{data.currentSelection[key]}</TableCell>
                  <TableCell>
                    {data.earliestWithIncomeAndNoPenalty[key]}
                  </TableCell>
                  <TableCell>{data.earliestPossible[key]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

// A tiny helper to build an empty row object
function makeEmptyRow() {
  return {
    retirementAge: "N/A",
    retirementDate: "N/A",
    qualifyingYearsOfService: "N/A",
    creditingYearsOfService: "N/A",
    distributionStartAge: "N/A",
    distributionStartDate: "N/A",
    monthlyIncomeFromPension: "N/A",
  };
}

export default FederalInformationTable;
